// Merge Phoenix native agend do legacy sekcí hlavního menu.
// Fáze 1: native položky se vkládají na ZAČÁTEK odpovídající sekce; položka s `group` se vloží do
// stejné vnořené skupiny (vytvoří se, pokud chybí). Sekce, kterou legacy (zatím) nemá, vznikne jako
// native-only (agendy nezmizí).
//
// Párování native ↔ legacy je na STABILNÍM, na jazyku nezávislém identifikátoru (`stableId` = Magic
// <SubItem ID>, např. `reservations` / `inventory-codebooks`), NE na lokalizovaném labelu. Díky tomu
// se menu nezdvojuje, i když se aktivní kultura Phoenixu a Magicu dočasně rozejdou.

import type { MenuGroupNode, MenuItemNode, MenuNode, MenuSection } from '@/shared/ui';

/** Deklarace native agendy do menu. */
export interface NativeMenuEntry {
  /** Navigační path — `onOpen` (native) na ni naviguje. */
  readonly id: string;
  readonly title: string;
  /**
   * Stabilní ID cílové root sekce (shodné s Magic `<SubItem ID>`, např. `reservations`) — na jazyku
   * NEZÁVISLÝ matching klíč proti `MenuSection.stableId`.
   */
  readonly section: string;
  /**
   * Lokalizovaný popisek sekce — použije se JEN pro native-only sekci (kterou legacy nemá). Když
   * sekce v legacy existuje, zobrazí se její (Magicem lokalizovaný) label. Default = `section`.
   */
  readonly sectionLabel?: string;
  /**
   * Stabilní ID cílové vnořené skupiny (shodné s Magic `<SubItem ID>`, např. `inventory-codebooks`).
   * Matching proti `MenuGroupNode.stableId`. Bez `group` jde položka na úroveň sekce.
   */
  readonly group?: string;
  /** Lokalizovaný popisek skupiny — jen pro native-only skupinu. Default = `group`. */
  readonly groupLabel?: string;
  /** GlobalName pro type-ikonu (volitelně). */
  readonly key?: string;
  /**
   * GlobalName legacy položky, kterou tato native agenda NAHRAZUJE. Odpovídající legacy list se
   * v menu skryje (dedup), takže nevzniká duplicita (např. native OP nahradí `BusinessCase.List`).
   */
  readonly replacesKey?: string;
}

function toNode(entry: NativeMenuEntry): MenuItemNode {
  return { t: 'item', id: entry.id, title: entry.title, key: entry.key, origin: 'native' };
}

/** Rekurzivně vyřadí legacy listy, jejichž GlobalName (`key`) nahrazuje některá native agenda. */
function stripReplaced(nodes: readonly MenuNode[], replaced: ReadonlySet<string>): MenuNode[] {
  const out: MenuNode[] = [];
  for (const node of nodes) {
    if (node.t === 'item') {
      if (node.origin !== 'native' && node.key && replaced.has(node.key)) continue;
      out.push(node);
    } else if (node.t === 'group') {
      out.push({ ...node, items: stripReplaced(node.items, replaced) });
    } else {
      out.push(node);
    }
  }
  return out;
}

/** Vloží native položky do položek sekce: ne-skupinové na začátek, skupinové do (příp. nové) skupiny. */
function mergeIntoSectionItems(
  sectionItems: readonly MenuNode[],
  entries: NativeMenuEntry[]
): MenuNode[] {
  const ungrouped = entries.filter((entry) => !entry.group).map(toNode);

  let items: MenuNode[] = [...sectionItems];
  const byGroup = new Map<string, NativeMenuEntry[]>();
  for (const entry of entries) {
    if (!entry.group) continue;
    const arr = byGroup.get(entry.group);
    if (arr) arr.push(entry);
    else byGroup.set(entry.group, [entry]);
  }

  for (const [groupId, groupEntries] of byGroup) {
    const nodes = groupEntries.map(toNode);
    const index = items.findIndex((node) => node.t === 'group' && node.stableId === groupId);
    if (index >= 0) {
      const existing = items[index] as MenuGroupNode;
      items[index] = { ...existing, items: [...nodes, ...existing.items] };
    } else {
      const group: MenuGroupNode = {
        t: 'group',
        id: `native-group-${groupId}`,
        stableId: groupId,
        title: groupEntries[0].groupLabel ?? groupId,
        items: nodes,
      };
      items = [group, ...items];
    }
  }

  return [...ungrouped, ...items];
}

export function mergeNativeIntoSections(
  sections: readonly MenuSection[],
  native: readonly NativeMenuEntry[]
): MenuSection[] {
  // 1) Skryj legacy položky, které native agenda nahrazuje (dedup napříč Magic ↔ Phoenix).
  const replaced = new Set<string>();
  for (const entry of native) {
    if (entry.replacesKey) replaced.add(entry.replacesKey);
  }
  const base =
    replaced.size === 0
      ? sections
      : sections.map((s) => ({ ...s, items: stripReplaced(s.items, replaced) }));

  if (native.length === 0) return base.map((s) => ({ ...s }));

  // 2) Native položky seskupené dle stabilního ID cílové sekce (zachovává pořadí deklarace).
  const bySection = new Map<string, NativeMenuEntry[]>();
  for (const entry of native) {
    const arr = bySection.get(entry.section);
    if (arr) arr.push(entry);
    else bySection.set(entry.section, [entry]);
  }

  const used = new Set<string>();
  const merged: MenuSection[] = base.map((s) => {
    const entries = s.stableId ? bySection.get(s.stableId) : undefined;
    if (!entries || !s.stableId) return s;
    used.add(s.stableId);
    return { ...s, items: mergeIntoSectionItems(s.items, entries) };
  });

  // 3) Sekce bez legacy protějšku → native-only (na konec, v pořadí prvního výskytu).
  for (const [sectionId, entries] of bySection) {
    if (used.has(sectionId)) continue;
    merged.push({
      id: `native-${sectionId}`,
      stableId: sectionId,
      label: entries[0].sectionLabel ?? sectionId,
      items: mergeIntoSectionItems([], entries),
    });
  }

  return merged;
}
